/* ndl-wishlist.js — replaces the Swish (Wishlist King) app.
 *
 * Stores the shopper's shortlist in localStorage. No account, no server, no
 * monthly fee — which is what feedback_shopify_native_infra requires (no hosted
 * backend for customer-facing features).
 *
 * KNOWN AND ACCEPTED LIMITATION: the list lives in ONE browser. Shortlist on a
 * phone and it is not on the laptop; clear site data and it is gone. Syncing
 * across devices needs customer-metafield writes, which needs Admin API, which
 * needs a server — a directive decision, not a coding one. See
 * docs/proposals/replace-apps-2026-08-16/ §E.
 *
 * Stores HANDLES, not numeric IDs: handles survive a re-import, and /products/
 * {handle}.js gives us live price and image without duplicating product data
 * into storage that would go stale.
 */
(function () {
  'use strict';
  var KEY = 'ndl_wishlist_v1';
  var MAX = 200;                      // a shortlist, not a second catalogue

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v.filter(function (h) { return typeof h === 'string'; }) : [];
    } catch (e) {
      return [];                      // corrupt or storage blocked (private mode)
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      return true;
    } catch (e) {
      return false;                   // quota or private mode — fail quiet, never throw
    }
  }

  var NDLWishlist = {
    all: read,
    has: function (handle) { return read().indexOf(handle) !== -1; },
    count: function () { return read().length; },

    toggle: function (handle) {
      var list = read();
      var i = list.indexOf(handle);
      if (i === -1) { list.unshift(handle); } else { list.splice(i, 1); }
      write(list);
      document.dispatchEvent(new CustomEvent('ndl:wishlist:change', {
        detail: { handle: handle, added: i === -1, count: list.length }
      }));
      return i === -1;
    },

    remove: function (handle) {
      var list = read().filter(function (h) { return h !== handle; });
      write(list);
      document.dispatchEvent(new CustomEvent('ndl:wishlist:change',
        { detail: { handle: handle, added: false, count: list.length } }));
    }
  };
  window.NDLWishlist = NDLWishlist;

  /* --- heart buttons ------------------------------------------------------ */
  function paint(btn) {
    var on = NDLWishlist.has(btn.getAttribute('data-handle'));
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('is-saved', on);
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
  }

  function paintAll() {
    document.querySelectorAll('[data-ndl-wishlist-toggle]').forEach(paint);
    document.querySelectorAll('[data-ndl-wishlist-count]').forEach(function (el) {
      var n = NDLWishlist.count();
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  // Delegated: collection grids lazy-load and paginate, so binding per-button
  // at load would miss every tile added afterwards.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ndl-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();              // tiles are wrapped in a link to the PDP
    NDLWishlist.toggle(btn.getAttribute('data-handle'));
    paint(btn);
  });

  document.addEventListener('ndl:wishlist:change', paintAll);
  document.addEventListener('DOMContentLoaded', paintAll);
  if (document.readyState !== 'loading') paintAll();

  /* --- wishlist page ------------------------------------------------------ */
  function money(cents, fmt) {
    // THOUSANDS SEPARATOR MATTERS. The store renders "Rs. 4,941"; without grouping
    // this showed "Rs. 2596", so the same product read differently on the wishlist
    // than on every other page. Small, but it looks like a different price.
    var whole = Math.floor(cents / 100);
    var paise = cents % 100;
    var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var v = paise ? grouped + '.' + String(paise).padStart(2, '0') : grouped;
    return (fmt || 'Rs. {{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, v);
  }

  // Fetch in small batches, not all at once. Promise.all over 200 handles fires 200
  // simultaneous requests: the browser queues them ~6 at a time anyway, Shopify may
  // rate-limit, and NOTHING renders until the slowest one returns. Batching renders
  // the first products almost immediately and keeps order stable.
  var BATCH = 6;

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function card(p, fmt) {
    var img = (p.featured_image || '').replace(/^\/\//, 'https://');
    var el = document.createElement('div');
    el.className = 'ndl-wl__card';
    el.innerHTML =
      '<a href="/products/' + p.handle + '">' +
        (img ? '<img src="' + img + '&width=500" alt="" loading="lazy">' : '') +
        '<span class="ndl-wl__title"></span>' +
      '</a>' +
      '<span class="ndl-wl__price"></span>' +
      '<button type="button" class="ndl-wl__remove" data-handle="' + p.handle + '">Remove</button>';
    el.querySelector('.ndl-wl__title').textContent = p.title;   // textContent, never innerHTML
    el.querySelector('.ndl-wl__price').textContent = money(p.price, fmt);
    return el;
  }

  window.NDLWishlistRender = function (root) {
    var list = NDLWishlist.all();
    var empty = root.querySelector('[data-ndl-empty]');
    var grid = root.querySelector('[data-ndl-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    if (!list.length) { if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;

    var fmt = root.getAttribute('data-money-format');
    var gone = [];
    var i = 0;

    function nextBatch() {
      if (i >= list.length) {
        // A handle that 404s was deleted or unpublished. Drop it rather than leaving
        // a tile the shopper cannot clear. Done AFTER all batches so the removals
        // cannot renumber a list still being walked.
        if (gone.length) gone.forEach(NDLWishlist.remove);
        if (!grid.children.length && empty) empty.hidden = false;
        return;
      }
      var slice = list.slice(i, i + BATCH);
      i += BATCH;
      Promise.all(slice.map(fetchProduct)).then(function (items) {
        items.forEach(function (p, n) {
          if (!p) { gone.push(slice[n]); return; }
          grid.appendChild(card(p, fmt));   // appended in slice order, so order holds
        });
        nextBatch();
      });
    }
    nextBatch();
  };

  // Bound ONCE, at module level. It used to be attached inside the render function,
  // which re-runs after every removal — so each remove added another listener and
  // the handler fired 2x, 3x, 4x. Delegated from document so it survives the grid
  // being emptied and rebuilt.
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('.ndl-wl__remove');
    if (!b) return;
    var root = b.closest('[data-ndl-wishlist-page]');
    if (!root) return;
    NDLWishlist.remove(b.getAttribute('data-handle'));
    window.NDLWishlistRender(root);
  });
})();
